import React, { useCallback, useState } from "react";

import { ArticleProps } from "../../../models";

import Table from "../../../components/table/Table";
import { usePagination } from "../../../hooks/usePagination";
import useUser from "../../../hooks/useUser";
import { limit } from "../Articles";
import ArticleTranscriptModal from "./ArticleTranscriptModal";
import ArticleViewModal from "./ArticleViewModal";
import { getArticleColumns } from "./table/articleColumns";

type ActionType = "view" | "download" | "transcript";
export type ActionFn = (id: string, action: ActionType) => void;

type ModalState =
  | { id: string; action: "view" }
  | { id: string; action: "transcript" }
  | null;

const ArticleTableContainer = ({
  articles,
  showPagination,
  totalArticles,
  offset,
  setOffset,
}: {
  articles: ArticleProps[];
  showPagination?: boolean;
  totalArticles?: number;
  offset?: number;
  setOffset?: (offset: number) => void;
}) => {
  const [modalState, setModalState] = useState<ModalState>(null);

  const handleArticleAction = useCallback<ActionFn>((id, action) => {
    if (action === "view" || action === "transcript") {
      setModalState({ id, action });
    }
  }, []);

  const closeModal = useCallback(() => setModalState(null), []);

  const { paginationData } = usePagination({
    total: totalArticles,
    offset,
    setOffset,
    limit,
    showPagination,
  });

  const { user } = useUser();
  const isSuperUser = user?.isSuper ?? false;

  const currentArticle = modalState
    ? articles.find((a) => a.articleId === modalState.id)
    : undefined;

  return (
    <>
      <Table<ArticleProps>
        columns={getArticleColumns(handleArticleAction, isSuperUser)}
        data={articles}
        keyExtractor={(a) => a.articleId}
        paginationData={paginationData}
      />

      {modalState?.action === "view" && currentArticle && (
        <ArticleViewModal
          articleId={currentArticle.articleId}
          articleUrl={currentArticle.s3ArticleUrl!}
          sourceId={currentArticle.sourceId}
          onRequestClose={closeModal}
        />
      )}

      {modalState?.action === "transcript" && currentArticle && (
        <ArticleTranscriptModal
          articleTitle={currentArticle.title}
          articleUrl={currentArticle.s3TranscriptionUrl!}
          onRequestClose={closeModal}
        />
      )}
    </>
  );
};

export default ArticleTableContainer;
